import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'glass'
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'icon'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant
  size?: Size
  /** Optional Material Symbols icon name (e.g. "edit", "delete"). */
  icon?: string
  loading?: boolean
  children?: ReactNode
}

const VARIANT: Record<Variant, string> = {
  primary:   'bg-primary text-on-primary hover:bg-primary/90 disabled:opacity-50',
  secondary: 'bg-surface-container-low text-on-surface border border-outline-variant hover:bg-surface-container hover:border-outline disabled:opacity-50',
  ghost:     'bg-transparent text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low disabled:opacity-50',
  danger:    'bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50',
  // Liquid Glass — DESIGN.md §14. For CTAs rendered inside glass surfaces
  // (modal footers, command palette actions, glass filter rows).
  glass:     'liquid-glass-pill text-on-surface hover:-translate-y-px transition-transform disabled:opacity-60',
}

const SIZE: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs font-medium',
  md: 'px-4 py-2 text-sm font-medium',
  // Touch target >= 48 px, body-size text — Schulportal-Dichte (DESIGN.md §17).
  lg: 'min-h-12 px-5 py-2.5 text-base font-medium',
  // Full-width touch CTA, the prototype `.dialog .actions .btn` (M27 T3 spec D/B7): pill, 54 px, 17 px / 600, gap 8.
  // Any caller can opt in.
  // Icon-only round button, 48 x 48 (Schulportal header: Einstellungen / Abmelden). Needs an aria-label from the caller.
  icon: 'size-12 shrink-0 p-0 text-base font-medium',
  xl: 'w-full min-h-[54px] px-6 text-[17px] font-semibold !gap-2',
}

// xl primary: the prototype shadow (0 8px 20px -8px). xl quiet (secondary / glass): glass fill + 1 px ring, no border (spec B7).
const XL_VARIANT: Partial<Record<Variant, string>> = {
  primary: 'bg-primary text-on-primary hover:bg-primary/90 disabled:opacity-50 shadow-[0_8px_20px_-8px_rgba(15,23,42,0.22)] dark:shadow-[0_8px_20px_-8px_rgba(0,0,0,0.5)]',
  secondary: 'bg-glass-a text-on-surface shadow-[inset_0_0_0_1px_var(--color-outline-variant)] hover:bg-surface-container-low disabled:opacity-50',
  glass: 'bg-glass-a text-on-surface shadow-[inset_0_0_0_1px_var(--color-outline-variant)] hover:bg-surface-container-low disabled:opacity-60',
}

/**
 * The single canonical Button. NEVER hand-roll button styling. See DESIGN.md § 8c + § 14.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'secondary', size = 'md', icon, loading, children, disabled, ...rest }, ref) {
  // .liquid-glass-pill sets no border-radius (index.css), so glass and the xl pill CTA
  // get rounded-full here; other sizes use rounded-lg.
  const radius = variant === 'glass' || size === 'xl' || size === 'icon' ? 'rounded-full' : 'rounded-lg'
  const skin = ((size === 'xl' || size === 'icon') && XL_VARIANT[variant]) || VARIANT[variant]
  return (
    <button
      {...rest}
      ref={ref}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-1.5 ${radius} transition-all disabled:cursor-not-allowed ${skin} ${SIZE[size]}`}
    >
      {loading ? (
        <span className={`material-symbols-outlined animate-spin ${size === 'xl' || size === 'icon' ? '!text-[22px]' : '!text-base'}`}>progress_activity</span>
      ) : icon ? (
        <span className={`material-symbols-outlined ${size === 'xl' || size === 'icon' ? '!text-[22px]' : '!text-base'}`}>{icon}</span>
      ) : null}
      {children}
    </button>
  )
})
